import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import CMSData from "aldoo-ra/CMS/cms-data"
import { cancelSubscription, submitUnsubscribeFeedback } from "aldoo-ra/Store/api"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import PubSub from "pubsub-js"

const ConfirmCancellationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  subscription,
  onShowStoreDialog,
  reason,
  feedback,
}) => {
  const [loading, setLoading] = useState(false)

  const texts = TextBundle({ id: "confirm_cancellation_dialog" })

  // Fetch unsubscribe reasons from CMS for dynamic mapping
  const { data: unsubscribeReasonsData = [] } = CMSData({
    contentType: "UnsubscribeReasons"
  });

  const reasonsData = unsubscribeReasonsData.length > 0 ? unsubscribeReasonsData[0] : null;
  const reasonsArray = reasonsData?.reasons || [];

  // Map reason IDs to feedback categories using CMS data
  const getCategoryFromReason = (reasonId) => {
    // Use the CMS data if available (array of {key, value})
    if (Array.isArray(reasonsArray)) {
      const reason = reasonsArray.find(item => item.key === reasonId);
      if (reason) {
        return reason.value;
      }
    }

    // Fallback to hardcoded mapping
    const categoryMap = {
      price: "The price is too high",
      technical: "I'm having technical difficulties",
      product: "I don't like the product",
      autobill: "I don't want to be auto-billed",
      no_need: "I no longer need it",
      other: "Other",
    }
    return categoryMap[reasonId] || "Other"
  }

  // Format the subscription end date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const endDate = subscription?.end_date ? formatDate(subscription.end_date) : "the end of your billing period"

  // Get subscription duration display
  const getSubscriptionDuration = () => {
    const days = subscription?.duration
    if (!days) return "Subscription"

    if (days === 7) return "Weekly"
    if (days === 30 || days === 31) return "Monthly"
    if (days === 365 || days === 366) return "Yearly"

    // Calculate based on days
    if (days >= 365) {
      const years = Math.floor(days / 365)
      return years === 1 ? "Yearly" : `${years} Year`
    }
    if (days >= 30) {
      const months = Math.floor(days / 30)
      return months === 1 ? "Monthly" : `${months} Month`
    }
    if (days >= 7) {
      const weeks = Math.floor(days / 7)
      return weeks === 1 ? "Weekly" : `${weeks} Week`
    }

    return `${days} Day`
  }

  // Get ALL store cancellation info and filter manually
  const { data: allStoreCancelInfo = [] } = CMSData({
    contentType: "StoreCancelSubscriptionInfo"
  })
  
  // Filter manually based on platform
  const storeCancelInfo = allStoreCancelInfo.filter(item => 
    subscription?.platform && 
    subscription.platform !== "Web" && 
    item.platform === subscription.platform.toLowerCase()
  )
  
  const storeInfo = storeCancelInfo.length > 0 ? storeCancelInfo[0] : null


  const performCancellation = async () => {
    // Check if this is a non-web platform subscription
    if (subscription?.platform && subscription.platform !== "Web") {
      if (storeInfo) {
        onClose() // Close the confirm dialog
        onShowStoreDialog?.(storeInfo) // Show store dialog at parent level
      } else {
        showMessageDialog({
          title: "Error",
          message: "Store cancellation information not available. Please contact support.",
          variant: "error",
        })
      }
      return
    }

    // Continue with web subscription cancellation
    setLoading(true)

    try {
      const subscriptionId = subscription.metadata?.subscription_id || subscription._id
      const response = await cancelSubscription(subscriptionId)

      if (response.result) {
        // Submit feedback if reason is provided
        if (reason) {
          try {
            await submitUnsubscribeFeedback({
              subscription_id: subscriptionId,
              product_id: subscription.pid || subscription.product_id,
              category: getCategoryFromReason(reason),
              user_notes: feedback || "",
            })
          } catch (feedbackError) {
            console.error("Failed to submit feedback:", feedbackError)
            // Don't block the cancellation flow if feedback submission fails
          }
        }

        // Close the confirmation dialog
        onClose()

        // Show success dialog
        showMessageDialog({
          title: "Subscription Cancelled",
          message: "Your subscription has been successfully cancelled. You will retain access until the end of your current billing period.",
          btnLabel: "OK",
          showCloseX: false,
          onConfirm: () => {
            onConfirm()
          }
        })

        // Refresh account data after a delay to ensure backend has updated
        setTimeout(() => {
          PubSub.publish("account-update")
        }, 1500)
      } else if (response.error) {
        // Handle error
        showMessageDialog({
          title: "Error",
          message: response.error,
          variant: "error",
        })
      }
    } catch (error) {
      showMessageDialog({
        title: "Error",
        message: "An unexpected error occurred. Please try again.",
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={texts?.title}
      isOpen={isOpen}
      onClose={onClose}
      variant="aqua_medium"
      className="md:max-w-[575px]"
    >
      <LoadingOverlay
        title={texts?.loading_title}
        message={texts?.loading_message}
        loading={loading}
      />

      <div className="max-w-[500px] md:max-w-full space-y-8 md:space-y-10 pt-10 md:pt-4 mx-auto md:mx-0">
        <Typography
          variant="info_18"
          className="text-text text-center md:text-left my-0"
        >
          {texts?.subtitle_1}
        </Typography>

        <div className="bg-primary_lt/70 rounded-[24px] p-6 space-y-2">
          <Typography
            variant="body_24_accent"
            className="text-text text-center my-0"
          >
            {subscription?.product?.name}
          </Typography>
          <Typography
            variant="info_18"
            className="text-text_secondary text-center my-0"
          >
            {getSubscriptionDuration()} · ${subscription?.product?.amount}
          </Typography>
        </div>

        <div className="space-y-2">
          <Typography
            variant="info_18_accent"
            className="text-text my-0"
          >
            {texts?.subtitle_2}
          </Typography>

          <ul className="list-disc pl-5 space-y-1 text-text">
            <li>
              <Typography variant="info_18_light">
                Your subscription will remain active until <span className="font-semibold">{endDate}</span>.
              </Typography>
            </li>
          </ul>
        </div>

        <div className="flex justify-center md:justify-end">
          <Button
            variant="primary_s"
            onClick={performCancellation}
            className="min-w-[110px]"
          >
            {texts?.confirm_btn_text}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmCancellationDialog
